import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Celebration, type CelebrationVariant } from '@fluent/ui';
import type { LessonStats } from './lessonReducer.js';
import { useCompletions, useStreak } from '../progress/hooks.js';
import type { StreakMilestone } from '../progress/level.js';

export interface LessonCompleteProps {
  stats: LessonStats;
  onContinue: () => void;
  /** Set when the user JUST crossed a streak milestone (3/7/14/30/60/100/365).
   *  Renders a milestone banner above the stats; null when nothing crossed. */
  streakMilestone?: StreakMilestone | null;
}

/**
 * "Fast" pace threshold — testable exercises × seconds-per-exercise.
 * 12s/ex is brisk for a beginner (typical pace is ~18-22s/ex), so finishing
 * under this bar earns the lightning variant. Phrase intros + dialogue are
 * already excluded from `totalExercises` upstream.
 */
const FAST_SECONDS_PER_EXERCISE = 12;

/**
 * LessonComplete — post-lesson celebration overlay with animated XP,
 * accuracy %, time, and the freshly-updated streak. Wraps the
 * `<Celebration>` primitive and animates the XP counter from 0 → target
 * over ~900ms; the streak number is read from React Query and reflects
 * the optimistic post-completion value within a render.
 */
export function LessonComplete({ stats, onContinue, streakMilestone }: LessonCompleteProps) {
  const { t } = useTranslation();
  const displayedXp = useCountUp(stats.xp, 900);
  const { data: streak } = useStreak();
  const { data: completions = {} } = useCompletions();
  const streakDays = streak?.current_streak ?? 0;

  const accuracyPct = Math.round(stats.accuracy * 100);
  const minutes = Math.floor(stats.elapsedSeconds / 60);
  const seconds = stats.elapsedSeconds % 60;
  const timeLabel = minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}`
    : `${seconds}s`;

  // Variant precedence (most to least specific):
  //   streak-milestone → first-ever lesson → perfect run → fast finish → default.
  // The optimistic-update on `completions` runs before this screen renders, so
  // `completions.length === 1` correctly identifies the user's first finish.
  // "fast" is gated to multi-exercise lessons so a lone phrase_intro doesn't
  // accidentally trigger it on a teaching-only template.
  const isFirstEver = Object.keys(completions).length === 1;
  const isFast =
    stats.totalExercises >= 4 &&
    stats.elapsedSeconds < stats.totalExercises * FAST_SECONDS_PER_EXERCISE;

  let variant: CelebrationVariant = 'default';
  let title: string;
  let subtitle: string;
  if (streakMilestone) {
    variant = 'streak';
    title = t('lesson.complete.streak_milestone_title', { days: streakMilestone });
    subtitle = t('lesson.complete.streak_milestone_subtitle', { days: streakMilestone });
  } else if (isFirstEver) {
    variant = 'first';
    title = t('lesson.complete.first_title');
    subtitle = t('lesson.complete.first_subtitle');
  } else if (stats.perfect) {
    variant = 'perfect';
    title = t('lesson.complete.title_perfect');
    subtitle = t('lesson.complete.subtitle_perfect');
  } else if (isFast) {
    variant = 'fast';
    title = t('lesson.complete.fast_title');
    subtitle = t('lesson.complete.fast_subtitle');
  } else {
    title = t('lesson.complete.title');
    subtitle = t('lesson.complete.subtitle');
  }

  return (
    <Celebration
      open
      variant={variant}
      title={title}
      subtitle={subtitle}
      stats={[
        { label: t('lesson.complete.stat_xp'), value: `+${displayedXp}` },
        { label: t('lesson.complete.stat_accuracy'), value: `${accuracyPct}%` },
        { label: t('lesson.complete.stat_streak'), value: `🔥 ${streakDays}` },
        { label: t('lesson.complete.stat_time'), value: timeLabel },
      ]}
      ctaLabel={t('lesson.complete.continue')}
      onContinue={onContinue}
    />
  );
}

/** Ease-out counter animation — RAF-driven, reduced-motion aware. */
function useCountUp(target: number, durationMs: number): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) {
      setValue(0);
      return;
    }
    if (typeof window === 'undefined') {
      setValue(target);
      return;
    }
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setValue(target);
      return;
    }

    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // cubic ease-out
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}
